
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import MainTable from "../../../../components/mainTable";
import { ModalType } from "../../../../shared/utils/enums";
import { MedicationInventory } from "../../../../interfaces/pharmacy";
import { services } from "../../../../api";
import { showModal, hideModal } from "../../../../connected-components/modal/reducer";
import { AddNewMedicationForm } from "./forms/AddNewMedicationForm";
import { ReorderMedicationForm } from "./forms/ReorderMedicationForm";
import { toastService } from "../../../../shared/utils/toast";
import { getPharmacyInventoryColumns } from "../../../../shared/tableColumns/pharmacyInventoryColumns";
import pharmacySuppliersApi from "../../../../api/pharmacy/suppliers";
import { Supplier } from "../../../../interfaces/pharmacy";

const PharmacyInventory: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<MedicationInventory[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [editingItem, setEditingItem] = useState<MedicationInventory | null>(null);

  const fetchInventory = async () => {
    setIsLoading(true);
    try {
      const response: any = await services.pharmacy.getInventory();
      if (response.result === "success") {
        setData(response.content.data);
        setTotal(response.content.total);
      }
    } catch (error) {
      console.error("Failed to fetch inventory:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await pharmacySuppliersApi.getSuppliers();
      if (response.result === "success") {
        setSuppliers(response.content);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchSuppliers();
  }, [page, rowsPerPage]);

  const handleAddNewSubmit = (data: any) => {
    console.log("New Medication Data:", data);
    toastService.showSuccessToast("Medication added successfully!");
    dispatch(hideModal());
    fetchInventory(); // Refresh the list
  };

  const handleEditSubmit = (data: any) => {
    console.log("Updated Medication Data:", data);
    toastService.showSuccessToast("Medication updated successfully!");
    dispatch(hideModal());
    setEditingItem(null);
    fetchInventory(); // Refresh the list
  };

  const handleOrderSubmit = (data: any) => {
    console.log("Re-order Data:", data);
    toastService.showSuccessToast("Medication re-ordered successfully!");
    dispatch(hideModal());
  };

  const openFormModal = (item?: MedicationInventory) => {
    const isEditing = !!item;
    setEditingItem(item || null);

    dispatch(
      showModal({
        type: ModalType.COMPONENT,
        title: isEditing ? "Edit Medication" : "Add New Medication",
        component: (
          <AddNewMedicationForm
            onSubmit={isEditing ? handleEditSubmit : handleAddNewSubmit}
            onCancel={() => dispatch(hideModal())}
            initialValues={item}
            suppliers={suppliers}
          />
        ),
      })
    );
  };

  const handleOrder = (row: MedicationInventory) => {
      dispatch(
        showModal({
            type: ModalType.COMPONENT,
            title: `Re-order ${row.medicationName}`,
            component: (
                <ReorderMedicationForm
                    onSubmit={handleOrderSubmit}
                    initialValues={row}
                />
            ),
        })
      )
  }

  const columns = getPharmacyInventoryColumns(
    openFormModal,
    handleOrder,
  );

  const handlePageChange = (event: { pageIndex: number; pageSize: number }) => {
    setPage(event.pageIndex);
    setRowsPerPage(event.pageSize);
  };

  return (
    <div className="p-4">
        <MainTable
            data={data}
            columns={columns}
            isLoading={isLoading}
            showPagination={true}
            totalData={total}
            pageSize={rowsPerPage}
            currentPage={page}
            onPageChanged={handlePageChange}
            showSearch={true}
            showAddButton={true}
            addButtonLabel="Add New Medication"
            onAddNewItemPress={() => openFormModal()}
        />
    </div>
  );
};

export default PharmacyInventory; 